import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useDeleteMeter } from '../hooks/useMeters';

const DeleteConfirmModal = forwardRef(({ onDelete }, ref) => {
  const [show, setShow] = useState(false);
  const [currentMeter, setCurrentMeter] = useState(null);
  
  const deleteMutation = useDeleteMeter();

  useImperativeHandle(ref, () => ({
    show: (meter) => {
      setCurrentMeter(meter);
      setShow(true);
    },
    hide: () => {
      setShow(false);
      setCurrentMeter(null);
    }
  }));

  const handleDelete = async () => {
    if (!currentMeter) return;

    try {
      await deleteMutation.mutateAsync(currentMeter.meterId);
      setShow(false);
      onDelete();
    } catch (error) {
      console.error('Error deleting meter:', error);
    }
  };

  return (
    <Modal 
      className="im-modal"
      
      show={show} 
      onHide={() => setShow(false)}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-2" />
          Confirm Delete
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {deleteMutation.error && (
          <Alert variant="danger" className="mb-3">
            {deleteMutation.error instanceof Error 
              ? deleteMutation.error.message 
              : 'An error occurred while deleting the meter'}
          </Alert>
        )}
        
        <p>Are you sure you want to delete this meter?</p>
        {currentMeter && (
          <div className="bg-light p-3 rounded">
            <p className="mb-1"><strong>Meter Name:</strong> {currentMeter.meterName}</p>
            <p className="mb-1"><strong>Meter ID:</strong> {currentMeter.meterId}</p>
            <p className="mb-0"><strong>Module:</strong> {currentMeter.module}</p>
          </div>
        )}
        <p className="text-danger mt-3 mb-0">
          <small>This action cannot be undone.</small>
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button 
          variant="outline-secondary" 
          onClick={() => setShow(false)}
          disabled={deleteMutation.isLoading}
        >
          Cancel
        </Button>
        <Button 
          variant="danger" 
          onClick={handleDelete}
          disabled={deleteMutation.isLoading}
        >
          {deleteMutation.isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                className="me-2"
              />
              Deleting...
            </>
          ) : (
            'Delete Meter'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

DeleteConfirmModal.displayName = 'DeleteConfirmModal';

export default DeleteConfirmModal;